import L from "leaflet";
import { iconHtml } from "./icons";
import { configureMapAttribution } from "./map-attribution";

const defaultCenter = [59.5667, 16.4167];
const siteTitle = "Dingtuna Föreningskarta";
const defaultDescription = "Upptäck föreningar, företag, platser och aktiviteter i Dingtuna.";

export default function publicMap() {
  const config = JSON.parse(document.getElementById("public-map-data")?.textContent || '{"pins":[]}');

  return {
    pins: config.pins || [],
    organizations: config.organizations || [],
    query: "",
    selectedType: "all",
    currentOrganizationSlug: "",
    currentLevelPath: "",
    selectedPin: null,
    areaBounds: null,
    mapMoved: false,
    sheetState: "peek",
    sheetDragHeight: null,
    sheetDragStartY: null,
    sheetDragStartHeight: null,
    mobileFiltersOpen: false,
    locationPending: false,
    queryChangeMuted: false,
    announcement: "Kartan laddas.",
    map: null,
    markers: new Map(),
    locationMarker: null,
    zoomControl: null,
    programmaticMove: false,

    init() {
      this.map = L.map(this.$refs.map, { zoomControl: false }).setView(defaultCenter, 12);
      L.tileLayer(config.tileUrl, { attribution: config.attribution, maxZoom: 19 }).addTo(this.map);
      configureMapAttribution(this.map);
      this.syncMapControls();
      this.placeAttribution();

      for (const pin of this.pins) {
        const marker = L.marker([pin.latitude, pin.longitude], {
          icon: this.createMarkerIcon(pin, false),
          keyboard: true,
          title: pin.name,
          alt: `Visa ${pin.name}`
        });
        marker.on("click", () => this.selectPin(pin.id));
        this.markers.set(pin.id, marker);
      }

      this.$watch("query", () => {
        if (this.queryChangeMuted) {
          this.queryChangeMuted = false;
          return;
        }
        this.filtersChanged();
      });
      window.addEventListener("popstate", () => this.restoreFromUrl(false));
      window.addEventListener("resize", () => {
        this.map.invalidateSize();
        this.syncMapControls();
        this.placeAttribution();
      });
      this.map.on("dragend", () => { this.mapMoved = true; });
      this.map.on("zoomend", () => {
        if (!this.programmaticMove) this.mapMoved = true;
      });
      this.map.on("moveend", () => { this.programmaticMove = false; });

      const restored = this.restoreFromUrl(true);
      this.refreshMarkers();
      if (!this.selectedPin) this.fitVisiblePins();
      if (restored) {
        this.announcement = this.pins.length
          ? `${this.placeCount(this.visiblePins.length)} visas på kartan.`
          : "Det finns inga publicerade platser på kartan ännu.";
      }
    },

    iconHtml,

    placeCount(count) {
      return `${count} ${count === 1 ? "plats" : "platser"}`;
    },

    clearQuerySilently() {
      if (!this.query) return;
      this.queryChangeMuted = true;
      this.query = "";
    },

    get levelPins() {
      return this.pins
        .filter(pin => pin.parentPath === this.currentLevelPath)
        .sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name, "sv"));
    },

    get hasSearchQuery() {
      return Boolean(this.query.trim());
    },

    get resultScopePins() {
      if (this.currentOrganizationSlug) {
        return this.pins
          .filter(pin => pin.organization?.slug === this.currentOrganizationSlug)
          .sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name, "sv"));
      }
      return this.hasSearchQuery
        ? [...this.pins].sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name, "sv"))
        : this.levelPins;
    },

    get availableTypes() {
      const types = new Map();
      const counts = new Map();
      for (const pin of this.resultScopePins) types.set(pin.type, pin.typeLabel);
      for (const pin of this.filterablePins) {
        counts.set(pin.type, (counts.get(pin.type) || 0) + 1);
      }
      return [...types]
        .map(([key, label]) => ({ key, label, count: counts.get(key) || 0 }))
        .sort((left, right) => left.label.localeCompare(right.label, "sv"));
    },

    get filterablePins() {
      const search = this.query.trim().toLocaleLowerCase("sv");
      return this.resultScopePins.filter(pin => {
        const text = `${pin.name} ${pin.organization?.name || ""} ${pin.address || ""} ${pin.descriptionText || ""} ${pin.typeLabel}`.toLocaleLowerCase("sv");
        const matchesText = !search || text.includes(search);
        const matchesArea = !this.areaBounds || this.areaBounds.contains([pin.latitude, pin.longitude]);
        return matchesText && matchesArea;
      });
    },

    get visiblePins() {
      return this.filterablePins.filter(pin => this.selectedType === "all" || pin.type === this.selectedType);
    },

    get levelTitle() {
      if (this.currentOrganization) return this.currentOrganization.name;
      if (!this.currentLevelPath) return "Utforska Dingtuna";
      return this.currentLevelPin?.name || "Utforska platser";
    },

    get currentOrganization() {
      if (!this.currentOrganizationSlug) return null;
      return this.organizations.find(organization => organization.slug === this.currentOrganizationSlug) || null;
    },

    get currentLevelPin() {
      if (!this.currentLevelPath) return null;
      return this.pins.find(pin => pin.fullPath === this.currentLevelPath) || null;
    },

    get resultsTitle() {
      if (!this.hasSearchQuery) return this.levelTitle;
      return this.currentOrganization || this.currentLevelPath
        ? `Sökresultat i ${this.levelTitle}`
        : "Sökresultat";
    },

    get scopeDescription() {
      return this.currentOrganization?.descriptionText || this.currentLevelPin?.descriptionText || "";
    },

    get currentScopeImage() {
      return this.currentOrganization?.logoPath || this.currentLevelPin?.imagePath || null;
    },

    get breadcrumbLabel() {
      const current = this.currentLevelPin;
      if (!current?.parentPath) return "Dingtuna";
      return this.pins.find(pin => pin.fullPath === current.parentPath)?.name || "Dingtuna";
    },

    get emptyTitle() {
      return this.hasSearchQuery || this.resultScopePins.length ? "Inga platser hittades" : "Inga publicerade platser ännu";
    },

    get emptyDescription() {
      if (this.hasSearchQuery) return "Prova ett annat sökord eller ändra de valda filtren.";
      return this.resultScopePins.length
        ? "Prova ett annat sökord, välj en annan typ eller visa hela kartnivån."
        : "När platser publiceras i portalen visas de här på kartan.";
    },

    get directionsUrl() {
      if (!this.selectedPin) return "#";
      const destination = encodeURIComponent(`${this.selectedPin.latitude},${this.selectedPin.longitude}`);
      return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    },

    get organizationLinks() {
      if (!this.selectedPin?.organization) return [];
      const organization = this.selectedPin.organization;
      const links = organization.websiteUrl ? [{ label: "Besök webbplats", url: organization.websiteUrl }] : [];
      for (const link of organization.links || []) {
        if (!links.some(item => item.url === link.url)) links.push(link);
      }
      return links;
    },

    get selectedHasChildren() {
      return this.selectedPin ? this.pins.some(pin => pin.parentId === this.selectedPin.id) : false;
    },

    get directionsArePrimary() {
      if (!this.selectedPin) return false;
      if (this.selectedPin.actionType === "openchildpins") return !this.selectedHasChildren;
      if (this.selectedPin.actionType === "externallink") return !this.selectedPin.actionUrl;
      return true;
    },

    get sheetClasses() {
      const mobileHeight = this.sheetState === "full"
        ? "h-[calc(100dvh_-_var(--safe-area-top)_-_4.75rem)]"
        : this.sheetState === "half"
          ? "h-[54dvh]"
          : "h-[calc(4rem_+_var(--safe-area-bottom))]";
      return `${mobileHeight} transition-[height] duration-300 ease-out motion-reduce:transition-none`;
    },

    get sheetStyle() {
      return this.sheetDragHeight === null || window.innerWidth >= 1024
        ? null
        : `height: ${this.sheetDragHeight}px; transition: none;`;
    },

    createMarkerIcon(pin, selected) {
      const size = selected ? 56 : 48;
      const iconSize = selected ? "size-6" : "size-5";
      const targetClasses = selected ? "public-map-marker-target is-selected" : "public-map-marker-target";
      return L.divIcon({
        className: "",
        html: `<span class="${targetClasses}"><span class="public-map-marker" style="--marker-color:${pin.markerColor}">${iconHtml(pin.markerIcon, iconSize)}</span></span>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size]
      });
    },

    placeAttribution() {
      this.map?.attributionControl?.setPosition("bottomright");
    },

    syncMapControls() {
      if (window.innerWidth >= 1024 && !this.zoomControl) {
        this.zoomControl = L.control.zoom({ position: "topright" }).addTo(this.map);
        return;
      }
      if (window.innerWidth < 1024 && this.zoomControl) {
        this.zoomControl.remove();
        this.zoomControl = null;
      }
    },

    refreshMarkers() {
      const visibleIds = new Set(this.visiblePins.map(pin => pin.id));
      for (const pin of this.pins) {
        const marker = this.markers.get(pin.id);
        if (visibleIds.has(pin.id) || this.selectedPin?.id === pin.id) {
          marker.setIcon(this.createMarkerIcon(pin, this.selectedPin?.id === pin.id));
          if (!this.map.hasLayer(marker)) marker.addTo(this.map);
        } else if (this.map.hasLayer(marker)) {
          marker.removeFrom(this.map);
        }
      }
    },

    filtersChanged() {
      if (!this.hasSearchQuery
        && this.selectedType !== "all"
        && !this.filterablePins.some(pin => pin.type === this.selectedType)) {
        this.selectedType = "all";
      }
      this.areaBounds = null;
      this.mapMoved = false;
      this.selectedPin = null;
      this.mobileFiltersOpen = false;
      this.refreshMarkers();
      this.fitVisiblePins();
      this.updateUrl(false);
      this.announcement = `${this.placeCount(this.visiblePins.length)} matchar sökningen.`;
      this.resetSheetScroll();
    },

    applyQuery() {},

    clearSearch() {
      if (!this.query) return;
      this.query = "";
      this.$nextTick(() => {
        const searchId = window.innerWidth < 1024 ? "map-search-mobile" : "map-search-desktop";
        document.getElementById(searchId)?.focus();
      });
    },

    setType(type) {
      if (this.selectedType === type) return;
      this.selectedType = type;
      this.mobileFiltersOpen = false;
      this.filtersChanged();
    },

    toggleMobileFilters() {
      this.mobileFiltersOpen = !this.mobileFiltersOpen;
    },

    resetFilters() {
      this.clearQuerySilently();
      this.selectedType = "all";
      this.areaBounds = null;
      this.mapMoved = false;
      this.selectedPin = null;
      this.mobileFiltersOpen = false;
      this.refreshMarkers();
      this.fitVisiblePins();
      this.updateUrl(true);
      this.announcement = `Alla ${this.placeCount(this.resultScopePins.length)} i den valda kartvyn visas.`;
    },

    searchThisArea() {
      this.areaBounds = this.map.getBounds();
      this.mapMoved = false;
      this.selectedPin = null;
      this.refreshMarkers();
      this.updateUrl(true);
      this.announcement = `${this.placeCount(this.visiblePins.length)} finns i det valda kartområdet.`;
    },

    selectPin(id, pushHistory = true) {
      const pin = this.pins.find(item => item.id === id);
      const belongsToOrganization = pin?.organization?.slug === this.currentOrganizationSlug;
      if (!pin || (this.currentOrganizationSlug ? !belongsToOrganization : !this.hasSearchQuery && pin.parentPath !== this.currentLevelPath)) return;
      if (this.hasSearchQuery && !this.currentOrganizationSlug) this.currentLevelPath = pin.parentPath;
      this.selectedPin = pin;
      this.sheetState = "half";
      this.mobileFiltersOpen = false;
      this.refreshMarkers();
      this.focusPin(pin, Math.max(this.map.getZoom(), 15));
      if (pushHistory) this.updateUrl(true);
      this.announcement = `${pin.name} har valts.`;
      this.resetSheetScroll();
    },

    closeDetails() {
      const previousId = this.selectedPin?.id;
      this.selectedPin = null;
      this.refreshMarkers();
      this.updateUrl(true);
      this.announcement = `${this.placeCount(this.visiblePins.length)} visas.`;
      this.resetSheetScroll();
      this.$nextTick(() => document.querySelector(`[data-pin-id="${previousId}"]`)?.focus());
    },

    openChildLevel() {
      if (!this.selectedPin || !this.pins.some(pin => pin.parentId === this.selectedPin.id)) return;
      const parent = this.selectedPin;
      this.currentOrganizationSlug = "";
      this.currentLevelPath = parent.fullPath;
      this.selectedPin = null;
      this.clearQuerySilently();
      this.selectedType = "all";
      this.areaBounds = null;
      this.mapMoved = false;
      this.sheetState = "half";
      this.mobileFiltersOpen = false;
      this.refreshMarkers();
      this.fitVisiblePins();
      this.updateUrl(true);
      this.announcement = `${parent.name} öppnades. ${this.placeCount(this.levelPins.length)} visas.`;
      this.resetSheetScroll();
    },

    goBackLevel() {
      const current = this.pins.find(pin => pin.fullPath === this.currentLevelPath);
      if (!current) return;
      this.currentLevelPath = current.parentPath;
      this.clearQuerySilently();
      this.selectedType = "all";
      this.areaBounds = null;
      this.mapMoved = false;
      this.selectedPin = current;
      this.sheetState = "half";
      this.mobileFiltersOpen = false;
      this.refreshMarkers();
      this.focusPin(current, Math.max(this.map.getZoom(), 14));
      this.updateUrl(true);
      this.announcement = `Tillbaka till ${this.currentLevelPath ? this.levelTitle : "Dingtuna"}.`;
      this.resetSheetScroll();
    },

    setSheetState(state) {
      this.sheetState = state;
      this.sheetDragHeight = null;
      if (state === "peek") this.mobileFiltersOpen = false;
      this.$nextTick(() => {
        this.map.invalidateSize();
        if (!this.selectedPin && state !== "full") this.fitVisiblePins();
      });
    },

    showMoreResults() {
      this.setSheetState(this.sheetState === "half" ? "full" : "half");
      this.resetSheetScroll();
    },

    showMoreMap() {
      this.setSheetState("peek");
    },

    startSheetDrag(event) {
      if (window.innerWidth >= 1024 || event.button !== 0 || event.target.closest("button, a, input")) return;
      const minimum = 64 + this.safeAreaInset("--safe-area-bottom");
      this.sheetDragStartY = event.clientY;
      this.sheetDragStartHeight = this.$refs.sheet?.getBoundingClientRect().height || minimum;
      this.sheetDragHeight = this.sheetDragStartHeight;
      event.currentTarget.setPointerCapture(event.pointerId);
    },

    moveSheetDrag(event) {
      if (this.sheetDragStartY === null) return;
      const minimum = 64 + this.safeAreaInset("--safe-area-bottom");
      const maximum = Math.max(minimum, window.innerHeight - this.safeAreaInset("--safe-area-top") - 76);
      this.sheetDragHeight = Math.min(maximum, Math.max(minimum, this.sheetDragStartHeight + this.sheetDragStartY - event.clientY));
    },

    endSheetDrag(event) {
      if (this.sheetDragStartY === null) return;
      const height = this.sheetDragHeight;
      const minimum = 64 + this.safeAreaInset("--safe-area-bottom");
      const halfHeight = window.innerHeight * 0.54;
      const fullHeight = Math.max(minimum, window.innerHeight - this.safeAreaInset("--safe-area-top") - 76);
      this.sheetDragStartY = null;
      this.sheetDragStartHeight = null;
      this.sheetDragHeight = null;
      if (this.$refs.sheetHandle?.hasPointerCapture(event.pointerId)) this.$refs.sheetHandle.releasePointerCapture(event.pointerId);
      if (height < (minimum + halfHeight) / 2) this.setSheetState("peek");
      else if (height < (halfHeight + fullHeight) / 2) this.setSheetState("half");
      else this.setSheetState("full");
    },

    cancelSheetDrag(event) {
      if (this.sheetDragStartY === null) return;
      this.sheetDragStartY = null;
      this.sheetDragStartHeight = null;
      this.sheetDragHeight = null;
      if (this.$refs.sheetHandle?.hasPointerCapture(event.pointerId)) this.$refs.sheetHandle.releasePointerCapture(event.pointerId);
    },

    resetSheetScroll() {
      this.$nextTick(() => {
        if (this.$refs.sheetScroll) this.$refs.sheetScroll.scrollTop = 0;
      });
    },

    fitVisiblePins() {
      const pins = this.visiblePins;
      if (!pins.length) {
        this.moveTo(defaultCenter, 12);
        return;
      }
      const bounds = L.latLngBounds(pins.map(pin => [pin.latitude, pin.longitude]));
      const padding = this.mapContentPadding();
      this.programmaticMove = true;
      this.map.fitBounds(bounds, {
        maxZoom: 15,
        paddingTopLeft: padding.topLeft,
        paddingBottomRight: padding.bottomRight
      });
    },

    mapContentPadding() {
      if (window.innerWidth >= 1024) {
        return { topLeft: [430, 32], bottomRight: [32, 32] };
      }

      const safeAreaTop = this.safeAreaInset("--safe-area-top");
      const safeAreaBottom = this.safeAreaInset("--safe-area-bottom");
      const topInset = 84 + safeAreaTop;
      const sheetHeight = this.sheetState === "full"
        ? window.innerHeight - safeAreaTop - 76
        : this.sheetState === "half"
          ? window.innerHeight * 0.54
          : 64 + safeAreaBottom;
      const bottomInset = Math.min(
        sheetHeight + 16,
        Math.max(64, window.innerHeight - topInset - 64)
      );

      return {
        topLeft: [24, topInset],
        bottomRight: [24, Math.round(bottomInset)]
      };
    },

    safeAreaInset(property) {
      const value = getComputedStyle(document.documentElement).getPropertyValue(property);
      return Number.parseFloat(value) || 0;
    },

    focusPin(pin, zoom) {
      const padding = this.mapContentPadding();
      const paddingOffset = L.point(padding.bottomRight)
        .subtract(L.point(padding.topLeft))
        .divideBy(2);
      const pinPoint = this.map.project([pin.latitude, pin.longitude], zoom);
      const center = this.map.unproject(pinPoint.add(paddingOffset), zoom);
      this.moveTo(center, zoom);
    },

    moveTo(latlng, zoom) {
      this.programmaticMove = true;
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reducedMotion) this.map.setView(latlng, zoom);
      else this.map.flyTo(latlng, zoom, { duration: 0.45 });
    },

    locateUser() {
      if (!navigator.geolocation || this.locationPending) {
        this.announcement = "Din webbläsare kan inte hämta din position.";
        return;
      }
      this.locationPending = true;
      this.announcement = "Hämtar din position.";
      navigator.geolocation.getCurrentPosition(
        position => {
          this.locationPending = false;
          const latlng = [position.coords.latitude, position.coords.longitude];
          if (this.locationMarker) this.locationMarker.removeFrom(this.map);
          this.locationMarker = L.circleMarker(latlng, {
            radius: 9,
            color: "#ffffff",
            weight: 3,
            fillColor: "#2563eb",
            fillOpacity: 1
          }).addTo(this.map).bindTooltip("Din position", { permanent: false });
          this.moveTo(latlng, 15);
          this.announcement = "Din position visas på kartan.";
        },
        () => {
          this.locationPending = false;
          this.announcement = "Din position kunde inte hämtas. Kontrollera webbläsarens platsbehörighet.";
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    },

    restoreFromUrl(announceInvalid) {
      const params = new URLSearchParams(window.location.search);
      const requestedOrganization = (params.get("org") || "").trim();
      const organization = requestedOrganization
        ? this.organizations.find(item => item.slug === requestedOrganization)
        : null;
      const validOrganization = Boolean(organization
        && this.pins.some(pin => pin.organization?.slug === organization.slug));
      const requestedView = (params.get("view") || "").replace(/^\/+|\/+$/g, "");
      const viewPin = requestedView ? this.pins.find(pin => pin.fullPath === requestedView) : null;
      const validView = !requestedView || Boolean(viewPin && this.pins.some(pin => pin.parentId === viewPin.id));
      const validScope = requestedOrganization ? validOrganization : validView;
      this.currentOrganizationSlug = validOrganization ? requestedOrganization : "";
      this.currentLevelPath = !requestedOrganization && validView ? requestedView : "";
      this.selectedPin = null;
      const requestedPin = params.get("pin");
      if (requestedPin) {
        if (this.currentOrganizationSlug) {
          const organizationPins = this.pins.filter(pin => pin.organization?.slug === this.currentOrganizationSlug);
          this.selectedPin = organizationPins.find(pin => pin.fullPath === requestedPin)
            || (organizationPins.filter(pin => pin.slug === requestedPin).length === 1
              ? organizationPins.find(pin => pin.slug === requestedPin)
              : null);
        } else {
          this.selectedPin = this.pins.find(pin => pin.parentPath === this.currentLevelPath && pin.slug === requestedPin) || null;
        }
      }
      this.clearQuerySilently();
      this.selectedType = "all";
      this.areaBounds = null;
      this.mapMoved = false;
      this.mobileFiltersOpen = false;
      if (window.innerWidth < 1024) this.sheetState = this.selectedPin ? "half" : "peek";
      if (this.map) {
        this.refreshMarkers();
        if (this.selectedPin) this.focusPin(this.selectedPin, 15);
        else this.fitVisiblePins();
      }
      this.resetSheetScroll();
      const invalidSelection = Boolean(requestedPin && !this.selectedPin);
      const needsNormalization = !validScope || invalidSelection || Boolean(validOrganization && requestedView);
      if (needsNormalization) {
        if (announceInvalid) this.announcement = "Den efterfrågade kartvyn är inte tillgänglig. Närmaste tillgängliga vy visas.";
        this.updateUrl(false);
      } else {
        this.updateDocumentMetadata();
      }
      return Boolean(validScope && !invalidSelection);
    },

    updateUrl(push) {
      const url = new URL(window.location.href);
      if (this.currentOrganizationSlug) {
        url.searchParams.set("org", this.currentOrganizationSlug);
        url.searchParams.delete("view");
      } else {
        url.searchParams.delete("org");
        if (this.currentLevelPath) url.searchParams.set("view", this.currentLevelPath);
        else url.searchParams.delete("view");
      }
      if (this.selectedPin) {
        url.searchParams.set("pin", this.currentOrganizationSlug ? this.selectedPin.fullPath : this.selectedPin.slug);
      }
      else url.searchParams.delete("pin");
      const method = push ? "pushState" : "replaceState";
      window.history[method]({}, "", `${url.pathname}${url.search}${url.hash}`);
      this.updateDocumentMetadata();
    },

    updateDocumentMetadata() {
      const scope = this.currentOrganization || this.currentLevelPin;
      const title = scope ? `${scope.name} – ${siteTitle}` : siteTitle;
      const description = scope?.descriptionText || defaultDescription;
      const canonical = new URL(window.location.pathname, window.location.origin);
      if (this.currentOrganizationSlug) canonical.searchParams.set("org", this.currentOrganizationSlug);
      else if (this.currentLevelPath) canonical.searchParams.set("view", this.currentLevelPath);

      document.title = title;
      const values = [
        ['meta[name="description"]', description],
        ['meta[property="og:title"]', title],
        ['meta[property="og:description"]', description],
        ['meta[property="og:url"]', canonical.href],
        ['meta[name="twitter:title"]', title],
        ['meta[name="twitter:description"]', description]
      ];
      for (const [selector, value] of values) document.querySelector(selector)?.setAttribute("content", value);
      document.querySelector('link[rel="canonical"]')?.setAttribute("href", canonical.href);
    }
  };
}
